// Purchase-package → count-unit conversion for the multi-vendor purchasing
// model (purchase orders, invoice capture, price updates).
//
// The inventory COUNT unit is the authoritative unit: everything a vendor
// sells us (cases, jugs, sleeves) is converted INTO it. All unit math is
// delegated to the Menu Recipe Costing Lab's engine at
// supabase/functions/menu-recipe-costing/units.mjs (canonical bases g/ml/each;
// it never guesses density or per-each sizes), imported here relatively so
// both sides can never drift. Like that engine, this module is pure ESM with
// no Deno APIs, so edge functions and the Node test runner share it.
//
// Nothing here throws on bad input: unresolved conversions come back as
// { qty: null, status: 'unresolved', reason } — never as 0, so a missing
// conversion can never silently cost or count as zero.

import {
  normalizeUnit,
  convertToBase,
  parsePackSize,
} from "../menu-recipe-costing/units.mjs";
import { canonicalPackWord, liftLeadingContainer, readPack, isContainerWord } from "./packText.mjs";

export { normalizeUnit, parsePackSize };

const ENGINE = "menu-recipe-costing/units.mjs";

// A normalized cost more than 10,000× the package price means the package
// quantity was ~0.0001 count units — a bad conversion, not a real price.
const IMPLAUSIBLE_COST_RATIO = 10000;

function round6(n) {
  return Math.round(n * 1e6) / 1e6;
}

/** Positive finite number, or null for anything else. */
function positiveNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** True for units that reduce to a canonical base (g/ml/each). */
function isMeasurable(unitNorm) {
  return unitNorm && (unitNorm.kind === "mass" || unitNorm.kind === "volume" || unitNorm.kind === "count");
}

// ── Container count units ─────────────────────────────────────────────────────
//
// A produce kitchen counts CASES on the shelf and buys CASES from the vendor.
// "case" reduces to no canonical base, so the engine calls it unmeasurable —
// but "how many cases is one case?" is not a question about size, it is
// container arithmetic, and the answer is one. Refusing it was the single
// reason every case-counted item read "Unresolved pack conversion" with a null
// quantity, no received amount and an unverified price.
//
// Only identity and containment are answered here. A container's CONTENTS are
// still never guessed: "case" → "lb" stays unresolved for the AI bridge.

/** The count unit names a container the engine knows, in any spelling. */
function containerCountWord(countUnit) {
  const canonical = canonicalPackWord(countUnit);
  if (!canonical) return null;
  // One container vocabulary: the engine's normalizeUnit list PLUS packText's
  // extra words (keg, pouch, crate, drum…). The two lists diverging meant an
  // item counted in kegs fell to unknown_unit while packText happily read
  // "2 kegs × 5 gal" — the same word, container in one classifier, mystery
  // in the other.
  if (normalizeUnit(canonical).kind === "container") return canonical;
  return isContainerWord(canonical) ? canonical : null;
}

// "4 bags × 12 bunches" — a count of OUTER containers, a separator, then what
// one of them holds. The engine's parser expects the separator directly after
// the leading number, so the outer container word is read here first.
const LEADING_CONTAINER = /^(\d+(?:[.,]\d+)?)\s+([a-zA-Z]+)\s*[-/xX×]/;

// ── Piece count units ─────────────────────────────────────────────────────────
//
// "bun", "patty", "tortilla" — the engine cannot measure them and they wrap
// nothing, but an item counted in one is counted in PIECES of the product.
// packText already treats a stated count of such pieces as an exact
// measurement on the PACK side ("88 apples", "4 packs × 12 buns"); the same
// word on the ITEM side is the same fact, and refusing it there was why every
// bun/patty/tortilla item read "unknown unit" before its pack text was even
// opened. Contents are still never guessed: only a stated count of the same
// piece, a generic count (each/ct/dozen), or the identity "one bun IS one
// bun" are answered here.

/** The piece noun a non-measurable, non-container count unit names. */
function pieceCountWord(countUnit) {
  const canonical = canonicalPackWord(countUnit);
  if (!canonical) return null;
  if (isMeasurable(normalizeUnit(canonical))) return null;
  if (containerCountWord(canonical)) return null;
  return canonical;
}

/**
 * How many of the count unit's container does the pack text itself state?
 * One reading shared by the identity guard and the count paths below.
 *
 * @returns {{ qty: number, chain: string, measurements: object[],
 *             perPiece: object|null }|null}
 */
function statedContainerCount(desc, countWord) {
  if (!desc) return null;

  // Outer container: "4 bags × 12 bunches" counted in bags → 4.
  const leading = desc.match(LEADING_CONTAINER);
  if (leading && canonicalPackWord(leading[2]) === countWord) {
    const n = positiveNumber(leading[1].replace(",", "."));
    if (n != null) {
      return { qty: n, chain: `pack "${desc}" → ${n} ${countWord}`, measurements: [], perPiece: null };
    }
  }

  // Inner container: the same pack counted in bunches → 4 × 12 = 48. Lifting
  // the outer word leaves a shape the engine's parser reads.
  let text = desc;
  if (leading && normalizeUnit(leading[2]).kind === "container") {
    text = `${leading[1]} × ${desc.slice(leading[0].length).trim()}`;
  }
  const parsed = parsePackSize(text);
  if (parsed.parsed && !parsed.special && parsed.unitNorm?.kind === "container"
    && canonicalPackWord(parsed.unitNorm.key) === countWord) {
    const qty = positiveNumber(parsed.packCount * parsed.unitQty);
    if (qty != null) {
      return { qty, chain: `pack "${desc}" → ${parsed.packCount} × ${parsed.unitQty} ${countWord}`, measurements: [], perPiece: null };
    }
  }

  // Named counted containers around a per-piece size: "24 × 6.8 oz bottles"
  // counted in bottles is 24 bottles — a deterministic package fact, exactly
  // as "24 × 6.8 oz" counted in EA is 24. The 163.2 oz total stays a
  // simultaneous measurement (pack.measures), never overwritten by the count.
  // A "per"-wrapper ("16 oz / bottle") is the package itself, not what the
  // leading number counts, so it never resolves here.
  const pack = readPack(desc);
  if (pack.read && pack.pieceCountSource === "inner_pack"
    && pack.containerPosition && pack.containerPosition !== "per"
    && pack.containerWord === countWord
    && positiveNumber(pack.pieceCount) != null) {
    return {
      qty: pack.pieceCount,
      chain: `pack "${desc}" → ${pack.pieceCount} ${countWord} × ${pack.unitQty} ${pack.unitRaw}`,
      measurements: pack.measures ?? [],
      perPiece: pack.perPiece ?? null,
    };
  }
  return null;
}

/**
 * How many of the count unit's container does one purchased package hold?
 * Returns null when the package does not name that container at all.
 *
 * @returns {{ qty: number, method: string, chain: string, measurements: object[],
 *             perPiece: object|null }
 *          |{ ambiguous: true, statedQty: number }
 *          |null}
 */
function containerPackageQty({ packDescription, purchaseUnit, countWord }) {
  const desc = packDescription != null ? String(packDescription).trim() : "";
  const stated = statedContainerCount(desc, countWord);
  // The pack's readable physical facts ride along whichever container answer
  // wins: a case-counted "12 × 32 oz" keeps its 384 oz (and 12-piece count)
  // as simultaneous measurements — they are what lets a container-unit need
  // be compared and covered through physical contents later.
  const packRead = desc ? readPack(desc) : null;
  const packMeasures = packRead?.read ? packRead.measures ?? [] : [];

  // The vendor sells the very container the shelf counts in.
  if (canonicalPackWord(purchaseUnit) === countWord) {
    // …unless the pack text explicitly counts SEVERAL of that same container:
    // purchase unit "bottle" over "24 × 6.8 oz bottles" is the divided-case-
    // price signature (the container twin of the piece path's rule). Which
    // package the vendor bills is not an arithmetic question. Contents in a
    // DIFFERENT container ("4 bags × 12 bunches" bought by the case) describe
    // what is inside and contradict nothing.
    if (stated && stated.qty > 1 + 1e-9) {
      return { ambiguous: true, statedQty: stated.qty };
    }
    return {
      qty: 1,
      method: "container_identity",
      chain: `purchase unit "${purchaseUnit}" is the count unit — 1 package = 1 ${countWord}`,
      measurements: packMeasures,
      perPiece: null,
    };
  }

  if (stated) {
    const measurements = stated.measurements.length ? stated.measurements : packMeasures;
    return { qty: stated.qty, method: "container_count", chain: stated.chain, measurements, perPiece: stated.perPiece };
  }
  return null;
}

/** Map the engine's convertToBase failure reasons onto this module's vocabulary. */
function mapEngineReason(engineReason) {
  switch (engineReason) {
    case "unknown_unit": return "unknown_unit";
    case "bad_qty": return "bad_pack";
    case "needs_each_size": return "needs_each_size";
    // This API takes no density input, so a mass↔volume gap is a hard
    // incompatibility here rather than a fixable bridge.
    case "needs_density":
    case "unsupported":
    default:
      return "incompatible_units";
  }
}

/**
 * Convert `totalQty` of `unitRaw` into the count unit, retrying ambiguous
 * "oz" as fluid ounces when the count unit is volumetric — the engine
 * defaults oz to mass and leaves this call to the caller (see units.mjs).
 *
 * @returns {{ ok: boolean, qty: number|null, reason: string|null, warnings: string[] }}
 *   qty is expressed in `countNorm` units (unrounded).
 */
function convertIntoCountUnit(totalQty, unitRaw, unitNorm, countNorm, eachSize) {
  const attempt = (unit) => convertToBase(totalQty, unit, countNorm.base, { eachSize });
  let res = attempt(unitRaw);
  let warnings = res.warnings;
  if (!res.ok && res.reason === "needs_density" && unitNorm.ambiguousOz && countNorm.base === "ml") {
    const asFluid = attempt("floz");
    if (asFluid.ok) {
      res = asFluid;
      warnings = [...asFluid.warnings, "Interpreted 'oz' as fluid ounces because the count unit is volumetric — verify against the product."];
    }
  }
  if (!res.ok) return { ok: false, qty: null, reason: mapEngineReason(res.reason), warnings };
  return { ok: true, qty: res.qty / countNorm.factor, reason: null, warnings };
}

// ── packageToCountUnitQty ─────────────────────────────────────────────────────

/**
 * How many count units does one purchased package contain?
 *
 * Evidence is tried in order, and the first success wins:
 *   0. container units – the count unit is a CONTAINER ("case", "bag",
 *                        "bunch", "bottle", "can", "BIB"). Container
 *                        arithmetic only: the vendor sells that same container
 *                        ('container_identity', 1 package = 1 count unit) or
 *                        the pack text says how many of it one package holds
 *                        ('container_count', "4 bags × 12 bunches" counted in
 *                        bunches = 48; "24 × 6.8 oz bottles" counted in
 *                        bottles = 24 — the sized-and-named inner container
 *                        IS a stated count of that container). A container's
 *                        CONTENTS are still never guessed, and a row claiming
 *                        to SELL the very container its pack text counts many
 *                        of ("bottle" over "24 × 6.8 oz bottles") is the
 *                        divided-price signature and stays unresolved
 *                        ('ambiguous_package') for a human.
 *   0b. piece units    – the count unit names a PIECE the engine cannot
 *                        measure ("bun", "patty"). The pack text's stated
 *                        count of that same piece (or a generic each/ct/dozen
 *                        count) measures the package ('pack_parsed'), and a
 *                        vendor selling the piece itself is one of it
 *                        ('piece_identity'). A row claiming BOTH — purchase
 *                        unit "bun" over a pack text counting 24 — is the
 *                        divided-price signature and stays unresolved
 *                        ('ambiguous_package') for a human, never averaged.
 *   1. 'pack_parsed'   – packDescription parsed by the engine's parsePackSize
 *                        ("4 × 1 gal", "4-1GAL", "24/16 oz") and the parsed
 *                        total measure (packCount × unitQty) converted into
 *                        the count unit. caseQuantity is NOT applied here —
 *                        the pack text describes the package as sold.
 *   1b. 'pack_piece_count' – the count unit is count-family and the pack text
 *                        states an explicit piece count alongside a weight or
 *                        volume: "20/12 OZ" is twenty 12-oz pieces, so an
 *                        EA-counted item gets 20 per package with NO
 *                        weight→each bridge. Explicit grammar outranks any
 *                        each-size estimate. Range/AVG sizes ("12/12-16 OZ
 *                        AVG") still carry an exact piece count.
 *   2. 'direct'        – purchaseUnit is itself measurable; one package is
 *                        caseQuantity (default 1) × purchaseUnit.
 *   3. 'fallback_each' – nothing measurable was found, but the count unit is
 *                        count-kind, so a package is counted as caseQuantity
 *                        (default 1) each. Counts packages only — no size is
 *                        ever guessed. With NO stated case quantity this is a
 *                        pure default: it stays usable for counting a shelf,
 *                        but `orderSafe: false` — a defaulted 1 may NEVER
 *                        authorize a purchase quantity (see Part VIII of the
 *                        package-basis rules; the hanger-steak six-case bug
 *                        was exactly this default flowing into order math).
 *
 * @param {object} input
 * @param {string} [input.packDescription] supplier pack text, e.g. "4 × 1 gal"
 * @param {string} [input.purchaseUnit]    unit the item is purchased in, e.g. "case", "gal"
 * @param {string} [input.countUnit]       authoritative inventory count unit, e.g. "gal"
 * @param {number} [input.caseQuantity]    how many SELLING units are in one
 *   package — 88 apples, 24 heads, 12 bottles. This is the extraction's own
 *   definition (menu-starter `case_quantity`), and it is a WEAKER signal than
 *   the pack text: one unlabelled integer against a description that names
 *   both the number and the thing. It is therefore consulted only when the
 *   pack text cannot be read (`direct`, `fallback_each`), and never when the
 *   count unit is the wrapper the selling units sit inside (path 0).
 * @param {{qty:number, unit:string}} [input.eachSize] explicit per-each size bridge
 * @returns {{ qty: number|null, status: 'valid'|'unresolved',
 *             reason: 'unknown_unit'|'needs_each_size'|'incompatible_units'|'bad_pack'|'ambiguous_package'|null,
 *             chain: string[], warnings: string[],
 *             orderSafe: boolean, evidence: string|null,
 *             perPiece: object|null, approximate: boolean,
 *             provenance: { method: 'pack_parsed'|'pack_piece_count'|'direct'
 *                                   |'fallback_each'|'container_identity'
 *                                   |'container_count'|'piece_identity'|null,
 *                           engine: 'menu-recipe-costing/units.mjs' } }}
 *   `orderSafe` says whether this quantity may drive a purchase calculation.
 *   Every explicit or deterministically derived reading is order-safe; the
 *   `fallback_each` DEFAULT of 1 (no stated case quantity) is not — it exists
 *   for counting packages on a shelf, never for buying them. `evidence` names
 *   the class: 'explicit_pack_text' | 'deterministic_derived' |
 *   'stored_case_quantity' | 'purchase_unit_identity' | 'fallback_default'.
 */
export function packageToCountUnitQty({ packDescription, purchaseUnit, countUnit, caseQuantity, eachSize } = {}) {
  const warnings = [];
  const chain = [];
  const provenance = { method: null, engine: ENGINE };
  const caseQty = positiveNumber(caseQuantity);

  const unresolved = (reason) => ({
    qty: null, status: "unresolved", reason, chain, warnings, provenance,
    orderSafe: false, evidence: null, perPiece: null, approximate: false,
    measurements: [],
  });
  const valid = (qty, method, { orderSafe = true, evidence = "deterministic_derived", perPiece = null, approximate = false, measurements = [] } = {}) => ({
    qty: round6(qty), status: "valid", reason: null, chain, warnings,
    orderSafe, evidence, perPiece, approximate, measurements,
    provenance: { ...provenance, method },
  });

  const countNorm = normalizeUnit(countUnit);
  if (!isMeasurable(countNorm)) {
    // ── Path 0: the count unit is a container ("case", "bag", "bunch") ─────
    const countWord = containerCountWord(countUnit);
    if (countWord) {
      const container = containerPackageQty({ packDescription, purchaseUnit, countWord });
      if (container?.ambiguous) {
        // The row claims to sell the very container the shelf counts while its
        // pack text counts many of them — the divided-case-price signature.
        // Which package the price belongs to is held for a human.
        warnings.push(`Purchase unit "${purchaseUnit}" is one ${countWord}, but the pack description counts ${container.statedQty} — confirm which package the price belongs to.`);
        return unresolved("ambiguous_package");
      }
      if (container) {
        chain.push(container.chain);
        if (caseQty != null && caseQty !== container.qty) {
          // case_quantity counts SELLING units in a case (88 apples, 24 heads
          // — see the input docs). When the count unit is the WRAPPER itself,
          // a count of what is inside it can never be a count of wrappers:
          // 88 apples is one case, not 88 cases.
          warnings.push(`Case quantity ${caseQty} counts what is inside the ${countWord}, not ${countWord}s — the package is ${container.qty} ${countWord}(s) by its own description.`);
        }
        return valid(container.qty, container.method, {
          evidence: container.method === "container_identity" ? "purchase_unit_identity" : "explicit_pack_text",
          measurements: container.measurements ?? [],
          perPiece: container.perPiece ?? null,
        });
      }
      warnings.push(`Package does not say how many ${countWord}(s) it holds; its contents are not the count unit.`);
      return unresolved("incompatible_units");
    }

    // ── Path 0b: the count unit names a PIECE of the product ("bun") ────────
    const pieceWord = pieceCountWord(countUnit);
    if (pieceWord) {
      const identity = canonicalPackWord(purchaseUnit) === pieceWord;
      const pieceDesc = packDescription != null ? String(packDescription).trim() : "";
      const pack = pieceDesc ? readPack(pieceDesc) : null;

      if (pack?.read && pack.kind === "count") {
        const packPiece = pack.pieceWord ?? null;
        if (packPiece && packPiece !== pieceWord) {
          warnings.push(`Pack "${pieceDesc}" counts ${packPiece}s, not ${pieceWord}s.`);
          return unresolved("incompatible_units");
        }
        // A generic count (each/ct/dozen) of a piece-counted item IS a count
        // of its pieces; the factor turns dozens into eaches.
        const total = round6(pack.totalQty * (pack.unitNorm?.factor ?? 1));
        chain.push(`pack "${pieceDesc}" → ${pack.packCount} × ${pack.unitQty} ${packPiece ?? pack.unitRaw ?? "each"}`);
        if (identity) {
          if (Math.abs(total - 1) <= 1e-9) {
            chain.push(`purchase unit "${purchaseUnit}" is the count unit — 1 package = 1 ${pieceWord}`);
            return valid(1, "piece_identity", { evidence: "purchase_unit_identity" });
          }
          // The row claims to sell the piece itself while its pack text
          // counts many of them — the signature of a divided case price.
          // Which package the vendor really bills is not an arithmetic
          // question; it is held for a human.
          warnings.push(`Purchase unit "${purchaseUnit}" is one ${pieceWord}, but the pack description counts ${total} — confirm which package the price belongs to.`);
          return unresolved("ambiguous_package");
        }
        chain.push(`= ${total} ${pieceWord}`);
        return valid(total, "pack_parsed", { evidence: "explicit_pack_text", measurements: pack.measures ?? [] });
      }

      // NOTE deliberately NOT resolved here: a mass/volume pack with an inner
      // count ("6/1.5 LB" counted in SLICES) does not state how many of the
      // NAMED piece are inside — six 1.5-lb packs of sliced cheese are not six
      // slices. Only a generic count-family unit (EA/CT/PC, path 1b below)
      // may read the inner count as its own count; a piece NOUN needs the
      // semantic layer or a human to bridge.

      if (identity) {
        // A weight/volume pack ("4 oz") sizes the piece, and unreadable text
        // contradicts nothing — the vendor sells the very piece the shelf
        // counts, so one package is one of it.
        chain.push(`purchase unit "${purchaseUnit}" is the count unit — 1 package = 1 ${pieceWord}`);
        return valid(1, "piece_identity", { evidence: "purchase_unit_identity" });
      }
      if (pack?.read) {
        warnings.push(`Pack "${pieceDesc}" measures ${pack.kind}; counting ${pieceWord}s needs a stated piece count.`);
        return unresolved("needs_each_size");
      }
      if (pieceDesc) {
        warnings.push(`Pack description "${pieceDesc}" could not be read as a count of ${pieceWord}s.`);
        return unresolved("bad_pack");
      }
    }

    warnings.push(`Count unit "${countUnit ?? ""}" is not a measurable unit (g/ml/each family).`);
    return unresolved("unknown_unit");
  }

  // Most-specific failure seen, used if every path fails.
  let firstReason = null;
  const noteFailure = (reason, warning) => {
    if (!firstReason) firstReason = reason;
    if (warning) warnings.push(warning);
  };

  // ── Path 1: read the pack description ───────────────────────────────────
  // Read through the shared packText reader, so this module and the price
  // comparison get the SAME measurement from the same string: the outer
  // container is lifted ("2 sacks × 25 lb" is 50 lb, not unreadable) and a
  // stated count of pieces measures as pieces ("4 bags × 12 bunches" is 48,
  // not the case_quantity's 4).
  const desc = packDescription != null ? String(packDescription).trim() : "";
  if (desc) {
    const pack = readPack(desc);
    if (pack.read) {
      const label = pack.pieceWord ?? pack.unitRaw;
      chain.push(`pack "${desc}" → ${pack.packCount} × ${pack.unitQty} ${label}`);

      // ── Path 1a: the vendor bills a MEASURE ("LB", "GAL") ──────────────────
      // A purchase unit that is itself a mass or volume unit says the vendor
      // sells and bills that measure: one package IS one pound, whatever
      // container the product ships in. The pack text ("6/10 LB CHUB")
      // describes the shipment, not the billed unit — reading it as the
      // package is how a $4.94/lb chuck line was stored as a 60 lb package at
      // $4.94, i.e. $0.08/lb (store 453, 2026-09-05). A stored case_quantity
      // (the case's contents, or a delivered weight an extractor misfiled)
      // multiplies nothing here either. Mass/volume only: a count-kind
      // purchase unit ("each") over "12 ct" keeps its existing reading, and a
      // measure that cannot reach the count unit (lb → each with no piece
      // size) falls through to the ordinary pack reading exactly as before.
      const billedMeasure = normalizeUnit(purchaseUnit);
      if (billedMeasure && (billedMeasure.kind === "mass" || billedMeasure.kind === "volume")) {
        const identity = convertIntoCountUnit(1, purchaseUnit, billedMeasure, countNorm, eachSize);
        if (identity.ok && identity.qty > 0) {
          chain.push(`purchase unit "${purchaseUnit}" is the billed measure — 1 package = 1 ${billedMeasure.key}; pack "${desc}" describes the shipment (${round6(pack.totalQty)} ${label}), not the billed unit`);
          chain.push(`= ${round6(identity.qty)} ${countNorm.key}`);
          warnings.push(...identity.warnings);
          if (caseQty != null && Math.abs(caseQty - 1) > 1e-9) {
            warnings.push(`Case quantity ${caseQty} describes the shipment, not the billed ${billedMeasure.key} — it does not change the package quantity.`);
          }
          return valid(identity.qty, "direct", {
            evidence: "purchase_unit_identity",
            perPiece: pack.perPiece ?? null,
            approximate: pack.approximate === true,
          });
        }
      }

      // ── Path 1b: explicit piece count for a count-family count unit ───────
      // "20/12 OZ" counted in EA is twenty pieces — stated, not bridged. The
      // grammar's own count outranks any each-size estimate, and a catch-
      // weight range ("12/12-16 OZ AVG") still counts exactly 12. Count-kind
      // packs ("12 ct", "15 doz") flow through the ordinary conversion below,
      // which reaches the same number via the engine.
      if (countNorm.kind === "count" && pack.pieceCount != null && pack.kind !== "count") {
        const qty = pack.pieceCount / countNorm.factor;
        chain.push(`explicit piece count: ${pack.pieceCount} pieces = ${round6(qty)} ${countNorm.key}`);
        if (pack.perPiece?.qty != null) {
          chain.push(`each piece ${pack.perPiece.approximate ? "≈" : "="} ${pack.perPiece.min != null && pack.perPiece.max != null ? `${pack.perPiece.min}–${pack.perPiece.max}` : pack.perPiece.qty} ${pack.perPiece.unit}`);
        }
        return valid(qty, "pack_piece_count", {
          evidence: "explicit_pack_text", perPiece: pack.perPiece ?? null,
          measurements: pack.measures ?? [],
        });
      }

      const conv = convertIntoCountUnit(pack.totalQty, pack.unitToken, pack.unitNorm, countNorm, eachSize);
      warnings.push(...conv.warnings);
      if (conv.ok && conv.qty > 0) {
        chain.push(`= ${round6(pack.totalQty * pack.unitNorm.factor)} ${pack.unitNorm.base} = ${round6(conv.qty)} ${countNorm.key}`);
        if (pack.approximate) {
          warnings.push(`Pack "${desc}" states a nominal size (range/average) — the converted quantity is approximate.`);
        }
        return valid(conv.qty, "pack_parsed", {
          evidence: "explicit_pack_text", perPiece: pack.perPiece ?? null, approximate: pack.approximate === true,
          measurements: pack.measures ?? [],
        });
      }
      noteFailure(conv.ok ? "bad_pack" : conv.reason,
        `Pack "${desc}" parsed as ${label} but could not be converted into count unit "${countUnit}".`);
      chain.push(`✕ ${label} does not convert into ${countNorm.key}`);
    } else if (pack.special === "number_can") {
      // "6/#10" — the cans' net contents are unstated, but the count of cans
      // is not. An item counted in eaches of the can gets that count.
      if (countNorm.kind === "count" && pack.pieceCount != null) {
        const qty = pack.pieceCount / countNorm.factor;
        chain.push(`pack "${desc}" → ${pack.pieceCount} cans = ${round6(qty)} ${countNorm.key}`);
        return valid(qty, "pack_piece_count", { evidence: "explicit_pack_text" });
      }
      noteFailure("unknown_unit", `Pack "${desc}" uses #-can sizing; the can's net contents are not stated.`);
    } else if (pack.reason === "unparseable_pack" || pack.reason === "no_pack_description") {
      noteFailure("bad_pack", `Pack description "${desc}" could not be parsed.`);
    } else {
      noteFailure("unknown_unit", `Pack "${desc}" has no measurable unit (parsed "${pack.unitRaw ?? desc}").`);
    }
  }

  // ── Path 2: the purchase unit is directly measurable ────────────────────
  const purchaseNorm = normalizeUnit(purchaseUnit);
  if (isMeasurable(purchaseNorm)) {
    const n = caseQty ?? 1;
    if (caseQty == null) warnings.push(`No case quantity — assumed 1 ${purchaseNorm.key} per package.`);
    chain.push(`${n} × ${purchaseNorm.key} per package`);
    const conv = convertIntoCountUnit(n, purchaseUnit, purchaseNorm, countNorm, eachSize);
    warnings.push(...conv.warnings);
    if (conv.ok && conv.qty > 0) {
      chain.push(`= ${round6(conv.qty)} ${countNorm.key}`);
      return valid(conv.qty, "direct", {
        evidence: caseQty != null ? "stored_case_quantity" : "purchase_unit_identity",
      });
    }
    noteFailure(conv.ok ? "bad_pack" : conv.reason, null);
  }

  // ── Path 3: count packages as each (count-kind count units only) ────────
  if (countNorm.kind === "count") {
    const n = caseQty ?? 1;
    warnings.push(`Pack size unresolved — counted ${n} package(s) as ${n} each; verify the count.`);
    chain.push(`${n} package(s) counted as ${n} each`);
    const qty = n / countNorm.factor;
    chain.push(`= ${round6(qty)} ${countNorm.key}`);
    // A STATED case quantity may order ONLY when the pack text is absent —
    // with no text, the extraction's column is the only voice. When pack text
    // EXISTS but did not convert ("40 LB" for an EA-counted item), the case
    // quantity is an unproven claim beside a real physical statement: a 40 LB
    // citrus case is not 1 lemon because case_quantity defaulted to 1, and it
    // is not provably 40 lemons because 40 also names the pounds. Such rows
    // stay non-authorizing for the semantic layer or a human. Absence of
    // evidence is never evidence of one: the defaulted 1 is a counting
    // convenience and must never become a purchase quantity.
    const textless = !desc;
    return valid(qty, "fallback_each", {
      orderSafe: caseQty != null && textless,
      evidence: caseQty == null
        ? "fallback_default"
        : textless ? "stored_case_quantity" : "stored_case_quantity_unverified",
    });
  }

  return unresolved(firstReason ?? "bad_pack");
}

// ── packageMeasurementRows ────────────────────────────────────────────────────

/**
 * The conversion's independent physical facts as `package_measurements` rows —
 * the shape packageBasis.extractPackageMeasurements and the SQL order gate
 * read. One package may carry several facts at once: "16/12 OZ" yields a
 * piece count of 16 (role 'piece_count') AND a 192 OZ total (role
 * 'vendor_pack'); neither erases the other. Approximate totals (catch-weight
 * ranges, AVG sizes) keep a reduced confidence so a nominal weight can rank
 * below an exact one without being lost.
 *
 * Deterministic writers call this at sync time; packageBasis itself never
 * re-parses pack text.
 */
export function packageMeasurementRows(conversion) {
  const rows = [];
  for (const measure of conversion?.measurements ?? []) {
    if (!measure || !(measure.totalQty > 0)) continue;
    if (measure.kind === "count") {
      rows.push({
        quantity: round6(measure.totalQty),
        // A count of NAMED containers keeps its noun ("24 × 6.8 oz bottles"
        // counts bottles), so it can satisfy a bottle-counted need and never
        // a can-counted one. Anonymous counts stay "each".
        unit: measure.pieceNoun ?? "each",
        role: "piece_count",
        source: "deterministic",
        confidence: 0.9,
        approximate: false,
      });
    } else if (measure.kind === "mass" || measure.kind === "volume") {
      rows.push({
        quantity: round6(measure.totalQty),
        unit: measure.unitToken ?? null,
        role: "vendor_pack",
        source: "deterministic",
        confidence: measure.approximate ? 0.7 : 0.9,
        approximate: measure.approximate === true,
      });
    }
  }
  return rows;
}

// ── normalizedCostFromPackage ─────────────────────────────────────────────────

/**
 * Cost per count unit from a package price and its converted quantity:
 * cost = packagePrice / qtyPerPackage, rounded to 6dp.
 *
 * Never throws. cost is null and plausible is false when the inputs cannot
 * produce a meaningful number (non-positive or non-finite price/qty). A
 * computable but absurd result (cost > 10,000× the package price, i.e. the
 * package held ~nothing) keeps its cost but is flagged implausible.
 *
 * @returns {{ cost: number|null, plausible: boolean, warnings: string[] }}
 */
export function normalizedCostFromPackage({ packagePrice, qtyPerPackage } = {}) {
  const price = Number(packagePrice);
  if (!Number.isFinite(price) || price <= 0) {
    return { cost: null, plausible: false, warnings: [`Package price must be a positive number (got ${packagePrice}).`] };
  }
  const qty = Number(qtyPerPackage);
  if (!Number.isFinite(qty) || qty <= 0) {
    return { cost: null, plausible: false, warnings: [`Package quantity in count units must be a positive number (got ${qtyPerPackage}).`] };
  }
  const cost = round6(price / qty);
  if (!Number.isFinite(cost)) {
    return { cost: null, plausible: false, warnings: [`Cost per count unit is not finite (${price} / ${qty}).`] };
  }
  if (cost > price * IMPLAUSIBLE_COST_RATIO) {
    return {
      cost, plausible: false,
      warnings: [`Cost per count unit (${cost}) is over ${IMPLAUSIBLE_COST_RATIO}× the package price (${price}) — the package quantity (${qty}) looks wrong.`],
    };
  }
  return { cost, plausible: true, warnings: [] };
}

// ── packagesForNeed ───────────────────────────────────────────────────────────

/**
 * How many packages to buy to cover a need, honoring a minimum order
 * quantity and the vendor's legal ordering step (both in packages). Pure
 * arithmetic — no unit parsing.
 *
 *   raw      = needQty / qtyPerPackage
 *   stepped  = ceil(raw / orderIncrement) × orderIncrement
 *   packages = max(moqPackages, stepped)
 *
 * An unstated increment means whole packages, except for cut-to-order vendors
 * (`allowsFractional`), where the exact quantity passes through. MOQ, order
 * increment, and fractional COUNTING are three separate facts — none implies
 * another. Mirrors orderPackagesForNeed (parAndMoq.mjs); the parity test pins
 * every implementation to the same answers.
 *
 * @returns {{ packages: number, quantityReceived: number, overage: number }}
 *   quantityReceived = packages × qtyPerPackage;
 *   overage = quantityReceived − max(0, needQty).
 */
export function packagesForNeed({ needQty, qtyPerPackage, moqPackages, allowsFractional = false, orderIncrement } = {}) {
  const need = Number(needQty);
  const qty = Number(qtyPerPackage);
  if (!Number.isFinite(need) || !Number.isFinite(qty) || need <= 0 || qty <= 0) {
    return { packages: 0, quantityReceived: 0, overage: 0 };
  }
  const incRaw = Number(orderIncrement);
  const increment = Number.isFinite(incRaw) && incRaw > 0 ? incRaw : allowsFractional ? null : 1;
  // 1e-9 absorbs float noise before the ceiling: 3 / 1.5 must be 2 steps, not 3.
  const snapUp = (value) => round6(Math.ceil(value / increment - 1e-9) * increment);

  const moqRaw = Number(moqPackages);
  const stated = Number.isFinite(moqRaw) && moqRaw > 0 ? moqRaw : null;
  const moq = increment === null
    ? (stated !== null ? Math.ceil(stated) : 0)
    : stated !== null
      ? snapUp(stated)
      : allowsFractional
        ? 0
        : snapUp(1);

  const packages = increment === null
    ? Math.max(moq, round6(need / qty))
    : Math.max(moq, snapUp(need / qty));
  const quantityReceived = round6(packages * qty);
  const overage = round6(quantityReceived - Math.max(0, need));
  return { packages, quantityReceived, overage };
}

// ── validatePurchasePriceUpdate ───────────────────────────────────────────────

/**
 * Gate a candidate normalized cost before it may replace the stored one.
 * Pure — decides only; the caller applies (or keeps the previous verified
 * cost on rejection).
 *
 * Acceptance requires ALL of:
 *   - matchedItemId === expectedItemId   (the invoice line matched the item
 *                                         we intended to update)
 *   - conversionStatus === 'valid'        (package → count-unit conversion OK)
 *   - packagePrice > 0, finite
 *   - normalizedCost > 0, finite, and ≤ 10,000× packagePrice
 *   - verificationStatus === 'verified'   (source is trusted)
 *   - effectiveDate is newer than current.effectiveDate, or current is null
 *
 * @param {object} input
 * @param {object} input.candidate { normalizedCost, conversionStatus, packagePrice,
 *   verificationStatus, effectiveDate, matchedItemId, expectedItemId }
 * @param {object|null} input.current { normalizedCost, effectiveDate } — the stored cost
 * @returns {{ accept: boolean,
 *             reason: 'not_matched'|'unresolved_conversion'|'non_positive_price'|
 *                     'implausible_price'|'unverified_source'|'stale_effective_date'|'accepted' }}
 */
export function validatePurchasePriceUpdate({ candidate, current } = {}) {
  const c = candidate || {};
  const reject = (reason) => ({ accept: false, reason });

  if (c.matchedItemId !== c.expectedItemId) return reject("not_matched");
  if (c.conversionStatus !== "valid") return reject("unresolved_conversion");

  const price = Number(c.packagePrice);
  if (!Number.isFinite(price) || price <= 0) return reject("non_positive_price");
  const cost = Number(c.normalizedCost);
  if (!Number.isFinite(cost) || cost <= 0) return reject("non_positive_price");
  if (cost > price * IMPLAUSIBLE_COST_RATIO) return reject("implausible_price");

  if (c.verificationStatus !== "verified") return reject("unverified_source");

  if (current && current.effectiveDate) {
    const currentDate = new Date(current.effectiveDate);
    const candidateDate = new Date(c.effectiveDate);
    // An unparseable candidate date can never be proven newer.
    if (!Number.isFinite(candidateDate.getTime())) return reject("stale_effective_date");
    if (Number.isFinite(currentDate.getTime()) && candidateDate <= currentDate) {
      return reject("stale_effective_date");
    }
  }

  return { accept: true, reason: "accepted" };
}
